import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api/v1';

async function authedFetch(path: string, token: string | null, init?: RequestInit): Promise<unknown> {
  const headers = new Headers(init?.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init?.body) headers.set('Content-Type', 'application/json');
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`${res.status}: ${body}`);
  }
  return res.json();
}

export const RetryPolicySchema = z.object({
  max_retries: z.number().int().min(0).max(5).default(2),
  low_confidence_threshold: z.number().min(0).max(1).default(0.65),
  on_low_confidence: z.enum(['retry', 'escalate', 'accept']).default('retry'),
  on_max_retries: z.enum(['fallback', 'cancel', 'hand_off']).default('fallback'),
});
export type RetryPolicy = z.infer<typeof RetryPolicySchema>;

export const DialogTemplateSchema = z.object({
  id: z.number(),
  tenant_id: z.number(),
  warehouse_id: z.number(),
  version: z.number(),
  is_active: z.boolean(),
  language: z.string(),
  voice_tts: z.string().nullable(),
  prompts: z.record(z.string()),
  retry_policy: RetryPolicySchema,
  commands: z.record(z.array(z.string())),
  created_at: z.string(),
  created_by_user_id: z.number().nullable(),
});
export type DialogTemplate = z.infer<typeof DialogTemplateSchema>;

export interface DialogTemplateInput {
  warehouse_id: number;
  language: string;
  voice_tts: string | null;
  prompts: Record<string, string>;
  retry_policy: RetryPolicy;
  commands: Record<string, string[]>;
}

export function useListDialogTemplates(warehouseId: number | null, token: string | null) {
  return useQuery({
    queryKey: ['dialogTemplates', warehouseId],
    queryFn: () =>
      authedFetch(`/admin/platform/dialog-templates?warehouse_id=${warehouseId}&limit=200`, token) as Promise<{
        items: DialogTemplate[];
        next_cursor: string | null;
      }>,
    enabled: warehouseId !== null && token !== null,
  });
}

export function useDialogTemplate(id: number | null, token: string | null) {
  return useQuery({
    queryKey: ['dialogTemplate', id],
    queryFn: () => authedFetch(`/admin/platform/dialog-templates/${id}`, token) as Promise<DialogTemplate>,
    enabled: id !== null && token !== null,
  });
}

export function useActiveDialogTemplate(warehouseId: number | null, token: string | null) {
  return useQuery({
    queryKey: ['dialogTemplate', 'active', warehouseId],
    queryFn: () =>
      authedFetch(`/admin/platform/dialog-templates/active?warehouse_id=${warehouseId}`, token).catch(() => null) as Promise<DialogTemplate | null>,
    enabled: warehouseId !== null && token !== null,
  });
}

export function useCreateDialogTemplate(token: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DialogTemplateInput) =>
      authedFetch('/admin/platform/dialog-templates', token, {
        method: 'POST',
        headers: { 'Idempotency-Key': crypto.randomUUID() },
        body: JSON.stringify(input),
      }) as Promise<DialogTemplate>,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['dialogTemplates', data.warehouse_id] });
      qc.invalidateQueries({ queryKey: ['dialogTemplate', 'active', data.warehouse_id] });
    },
  });
}

export function useActivateDialogTemplate(token: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      authedFetch(`/admin/platform/dialog-templates/${id}/activate`, token, {
        method: 'POST',
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      }) as Promise<{ id: number; is_active: boolean }>,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dialogTemplates'] });
      qc.invalidateQueries({ queryKey: ['dialogTemplate'] });
    },
  });
}
